import React, { useCallback, useEffect, useState } from 'react';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import { Check, X, ImageOff, Clock, ShieldCheck, Ban, AlertTriangle, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { key: 'pending', label: 'Pending', icon: Clock },
  { key: 'approved', label: 'Approved', icon: ShieldCheck },
  { key: 'rejected', label: 'Rejected', icon: Ban },
];

const AdminPhotoApprovals = ({ onClose, userId = null, embedded = false }) => {
  const { sessionToken } = useUnifiedAuth();
  const [status, setStatus] = useState('pending');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status });
      if (userId) params.append('user_id', userId);
      if (flaggedOnly) params.append('flagged', 'true');
      const res = await fetch(`${BACKEND_URL}/api/admin/photo-approvals?${params}`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      const data = await res.json();
      setPhotos(data.photos || []);
      setPendingCount(data.pending_count || 0);
      setFlaggedCount(data.flagged_count || 0);
    } catch (err) {
      console.error('Error fetching photo approvals:', err);
    } finally {
      setLoading(false);
    }
  }, [status, userId, flaggedOnly, sessionToken]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const review = async (photoId, action) => {
    let body;
    if (action === 'reject') {
      const reason = window.prompt('Why is this photo rejected? (shown to the provider)', 'Photo quality is too low');
      if (reason === null) return;
      body = JSON.stringify({ reason });
    }
    setBusyId(photoId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/photo-approvals/${photoId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'application/json' },
        body,
      });
      if (res.ok) {
        setPhotos((prev) => prev.filter((p) => p.id !== photoId));
        setPendingCount((c) => Math.max(0, c - 1));
      }
    } catch (err) {
      console.error(`Error on photo ${action}:`, err);
    } finally {
      setBusyId(null);
    }
  };

  const body = (
    <div className="p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {STATUS_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = status === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setStatus(tab.key)}
              data-testid={`photo-status-tab-${tab.key}`}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                active ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {tab.key === 'pending' && pendingCount > 0 && (
                <span className="rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">{pendingCount}</span>
              )}
            </button>
          );
        })}
        <button
          onClick={() => setFlaggedOnly((v) => !v)}
          data-testid="photo-flagged-filter"
          aria-pressed={flaggedOnly}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
            flaggedOnly ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
          }`}
        >
          <AlertTriangle className="w-4 h-4" />
          Flagged only
          {flaggedCount > 0 && (
            <span className="rounded-full bg-white/90 px-1.5 text-[10px] font-bold text-amber-700">{flaggedCount}</span>
          )}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500" data-testid="photo-approvals-loading">Loading photos…</p>
      ) : photos.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center" data-testid="photo-approvals-empty">
          <ImageOff className="mx-auto mb-3 h-8 w-8 text-slate-300" />
          <p className="text-sm text-slate-500">No {status} photos.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="photo-approvals-grid">
          {photos.map((photo) => (
            <div
              key={photo.id}
              data-testid={`photo-approval-card-${photo.id}`}
              className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
            >
              <div className="aspect-video bg-slate-100">
                <img
                  src={`${BACKEND_URL}${photo.file_url}?auth=${sessionToken}`}
                  alt={photo.owner_name || 'Provider photo'}
                  loading="lazy"
                  className="h-full w-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    photo.image_type === 'gallery' ? 'bg-indigo-50 text-indigo-700' : 'bg-teal-50 text-teal-700'
                  }`}>
                    {photo.image_type === 'gallery' ? 'Facility' : 'Profile'}
                  </span>
                  {photo.category && <span className="text-[11px] text-slate-400">{photo.category}</span>}
                </div>
                <p className="mt-2 truncate font-semibold text-slate-900">{photo.owner_name || 'Unknown provider'}</p>
                <p className="truncate text-xs text-slate-500">{photo.owner_email}</p>

                {(photo.auto_flag_labels || []).length > 0 && (
                  <div className="mt-3 rounded-xl bg-amber-50 p-2.5" data-testid={`photo-flags-${photo.id}`}>
                    <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-700">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Auto-check {typeof photo.quality_score === 'number' ? `· score ${photo.quality_score}/100` : ''}
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {photo.auto_flag_labels.map((label) => (
                        <li key={label} className="text-xs text-amber-800">• {label}</li>
                      ))}
                    </ul>
                    {photo.duplicate_of && (
                      <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-rose-700">
                        <Copy className="w-3 h-3" />
                        {photo.duplicate_is_same_owner
                          ? 'Already in this provider\u2019s gallery'
                          : `Also uploaded by ${photo.duplicate_owner || 'another provider'}`}
                      </p>
                    )}
                  </div>
                )}
                {(photo.auto_flag_labels || []).length === 0 && photo.approval_status === 'pending' && (
                  <p className="mt-3 text-xs font-semibold text-emerald-700" data-testid={`photo-clean-${photo.id}`}>
                    Auto-check passed
                  </p>
                )}
                {photo.rejection_reason && (
                  <p className="mt-2 text-xs text-rose-600">Rejected: {photo.rejection_reason}</p>
                )}

                {status !== 'approved' && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      onClick={() => review(photo.id, 'approve')}
                      disabled={busyId === photo.id}
                      data-testid={`photo-approve-btn-${photo.id}`}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <Check className="w-4 h-4" /> Approve
                    </button>
                    {status !== 'rejected' && (
                      <button
                        onClick={() => review(photo.id, 'reject')}
                        disabled={busyId === photo.id}
                        data-testid={`photo-reject-btn-${photo.id}`}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                      >
                        <X className="w-4 h-4" /> Reject
                      </button>
                    )}
                  </div>
                )}
                {status === 'approved' && (
                  <button
                    onClick={() => review(photo.id, 'reject')}
                    disabled={busyId === photo.id}
                    data-testid={`photo-takedown-btn-${photo.id}`}
                    className="mt-4 w-full rounded-lg bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                  >
                    Take down
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (embedded) {
    return <div data-testid="photo-approvals-embedded">{body}</div>;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 p-3 sm:p-6">
      <div className="w-full max-w-6xl rounded-3xl bg-white shadow-2xl" data-testid="photo-approvals-modal">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Photo Approvals</h2>
            <p className="text-xs text-slate-500">Provider profile & facility photos stay private until approved</p>
          </div>
          <button
            onClick={onClose}
            data-testid="photo-approvals-close-btn"
            className="grid h-9 w-9 place-items-center rounded-full text-slate-400 hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {body}
      </div>
    </div>
  );
};

export default AdminPhotoApprovals;
